// screens/ProfileScreen.tsx — Profilo / Impostazioni (§5.8 / V27).
//
// P6 STEP 3a (2026-06-06): rebuilt from the theme/a11y-only stub into the full
// identity surface per redesign spec §5.8.
// V28 Phase F1 (2026-06-07): light-only lockdown — the "Aspetto" Chiaro/Scuro/
// Auto segmented control was dropped (the app is light-only now). DO NOT
// reintroduce an Aspetto / Tema / Dislessia-as-theme toggle: the dyslexia and
// contrast controls below are ACCESSIBILITY, not a theme picker.
// V28 S6 PROFILO (profilo.html): restyled to the V28 pixel contract —
//   • Identity card — rounded-SQUARE gradient avatar (52px / radius 14) + name +
//     school line + exam-countdown eyebrow.
//   • Accessibilità — Font per dislessia / Alto contrasto / Riduci animazioni
//     (a11y toggles — they STAY here, they are NOT a theme).
//   • Account — "Modifica profilo" (name · data esame, registration-style edit)
//     / Informativa AI / Privacy e dati (collapsibles).
//   • Esci (rose) → login. Version footer "AI Tutor · Autoscuola AI · v2.0".
// The bare inline <input type=date> is gone: name + exam-date editing now lives
// behind the "Modifica profilo" row (the registration-page flow), matching the
// prototype's Account chevron rows.
// Mobile-first per build-contract §B: sections stack; every control ≥44px tap
// target; no horizontal scroll at 320px. Light-only; hamburger LEFT; no back
// arrow (gesture-back).

import type { FormEvent, JSX, RefObject } from 'react';
import { useEffect, useRef, useState } from 'react';
import { ApiError } from '../api/client';
import { authApi, type RecoveryKit } from '../api/auth';
import { studentProfileApi } from '../api/student-profile';
import { ExamDateField, type ParsedExamDate } from '../components/ExamDateField';
import { Screen } from '../components/Screen';
import { TopChrome } from '../components/TopChrome';
import { useShellNav, useShellNavigationLock } from '../components/AppFrame';
import { StudentLogoutModal } from '../components/StudentLogoutModal';
import { PasswordField } from '../components/PasswordField';
import { RecoveryKitDisplay } from '../components/RecoveryKitDisplay';
import { useTokens, useTheme } from '../state/theme';
import {
  getStoredProfile,
  examCountdownLabel,
  prepareStudentProfilePatch,
  replaceStoredProfileFromPatchResult,
  toDateInputValue,
} from '../state/profile';

type ProfileSaveState =
  | { kind: 'idle' }
  | { kind: 'unchanged' }
  | { kind: 'pending' }
  | { kind: 'success' }
  | { kind: 'conflict' }
  | { kind: 'retryable_failure' }
  | {
      kind: 'cache_failure';
      reason: 'missing_version' | 'write_after_updated' | 'write_after_conflict';
    };

type ProfileValidationField = null | 'firstName' | 'examDate';

interface ActiveProfileRequest {
  controller: AbortController;
  identity: symbol;
}

interface ProfileScreenProps {
  reloadPage?: () => void;
}

const PROFILE_PANEL_ID = 'modifica-profilo-panel';
const RECOVERY_PANEL_ID = 'recovery-kit-panel';
const DATE_HELP = 'La data d’esame aggiorna il conto alla rovescia e i promemoria di ripasso. Puoi cambiarla quando vuoi.';
const PRIVACY_DISCLOSURE = 'Il tuo nome e la data d’esame sono salvati nel tuo account. Servono per il profilo, il conto alla rovescia e i promemoria di ripasso. Le domande al Prof e i progressi di studio sono salvati per personalizzare il percorso e permettere alla tua autoscuola di seguire i tuoi progressi. Premendo “Esci” cancelli da questo dispositivo l’accesso e i dati di studio salvati qui; il tuo account e il profilo restano salvati nel servizio usato dalla tua autoscuola.';
const NAME_VALIDATION = 'Inserisci un nome da 1 a 100 caratteri.';
const DATE_VALIDATION = 'Inserisci una data d’esame valida oppure lascia il campo vuoto.';

function reloadBrowserPage(): void {
  window.location.reload();
}

// ---------------------------------------------------------------------------
// Small section heading (mono, faint, uppercased) — reused across sections.
// ---------------------------------------------------------------------------
function SectionLabel({ children }: { children: string }): JSX.Element {
  const t = useTokens();
  return (
    <h2
      style={{
        fontFamily:    t.fonts.mono,
        fontSize:      11,
        fontWeight:    600,
        color:         t.textFaint,
        letterSpacing: 0.6,
        textTransform: 'uppercase',
        margin:        '0 4px 4px',
      }}
    >
      {children}
    </h2>
  );
}

// ---------------------------------------------------------------------------
// A single labeled toggle row — reusable for the a11y toggles.
// ---------------------------------------------------------------------------
interface ToggleRowProps {
  label: string;
  hint?: string;
  value: boolean;
  onChange: (v: boolean) => void;
}

function ToggleRow({ label, hint, value, onChange }: ToggleRowProps): JSX.Element {
  const t = useTokens();
  return (
    <button
      onClick={() => onChange(!value)}
      role="switch"
      aria-checked={value}
      style={{
        width:        '100%',
        minHeight:    44,
        background:   t.surface,
        border:       `1px solid ${t.line}`,
        borderRadius: t.radius.md,
        padding:      '12px 14px',
        display:      'flex',
        alignItems:   'center',
        gap:          12,
        cursor:       'pointer',
        textAlign:    'left',
      }}
    >
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontFamily: t.fonts.body, fontSize: 14, fontWeight: 600, color: t.text }}>{label}</div>
        {hint && (
          <div style={{ fontFamily: t.fonts.body, fontSize: 12, color: t.textMuted, marginTop: 2, lineHeight: 1.4 }}>
            {hint}
          </div>
        )}
      </div>
      {/* Visual toggle knob */}
      <div
        style={{
          width:        36,
          height:       20,
          borderRadius: 999,
          background:   value ? t.accent : t.surface2,
          border:       `1px solid ${value ? t.accent : t.lineStrong}`,
          position:     'relative',
          transition:   'background .15s',
          flexShrink:   0,
        }}
      >
        <div
          style={{
            width:        14,
            height:       14,
            borderRadius: 999,
            background:   value ? (t.mode === 'dark' ? '#1a1510' : '#fff') : t.text,
            position:     'absolute',
            top:          2,
            left:         value ? 18 : 2,
            transition:   'left .15s',
          }}
        />
      </div>
    </button>
  );
}

// ---------------------------------------------------------------------------
// Collapsible info row (Informativa AI / Privacy e dati). Pure disclosure —
// no external link, content lives inline so it works offline.
// ---------------------------------------------------------------------------
function InfoDisclosure({
  label,
  testId,
  children,
}: {
  label: string;
  testId: string;
  children: JSX.Element;
}): JSX.Element {
  const t = useTokens();
  const [open, setOpen] = useState(false);
  return (
    <div
      style={{
        background:   t.surface,
        border:       `1px solid ${t.line}`,
        borderRadius: t.radius.md,
        overflow:     'hidden',
      }}
    >
      <button
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        data-testid={testId}
        style={{
          width:        '100%',
          minHeight:    44,
          background:   'transparent',
          border:       'none',
          padding:      '12px 14px',
          display:      'flex',
          alignItems:   'center',
          gap:          12,
          cursor:       'pointer',
          textAlign:    'left',
        }}
      >
        <span style={{ flex: 1, fontFamily: t.fonts.body, fontSize: 14, fontWeight: 600, color: t.text }}>
          {label}
        </span>
        <span
          aria-hidden
          style={{
            fontFamily: t.fonts.mono,
            fontSize:   12,
            color:      t.textMuted,
            transform:  open ? 'rotate(90deg)' : 'rotate(0deg)',
            transition: 'transform .15s',
          }}
        >
          ›
        </span>
      </button>
      {open && (
        <div
          style={{
            padding:    '0 14px 14px',
            fontFamily: t.fonts.body,
            fontSize:   13,
            color:      t.textMuted,
            lineHeight: 1.6,
          }}
        >
          {children}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Account chevron row — a tappable card row with a leading icon, a bold label,
// an optional muted sublabel on the right, and a chevron. Matches profilo.html
// `.row` (lines 62-77). Used for "Modifica profilo" (expands inline).
// ---------------------------------------------------------------------------
function ChevronRow({
  icon,
  label,
  sublabel,
  open,
  testId,
  onClick,
  buttonRef,
  disabled,
  controls,
}: {
  icon: JSX.Element;
  label: string;
  sublabel?: string;
  open?: boolean;
  testId?: string;
  onClick: () => void;
  buttonRef?: RefObject<HTMLButtonElement | null>;
  disabled?: boolean;
  controls?: string;
}): JSX.Element {
  const t = useTokens();
  return (
    <button
      type="button"
      ref={buttonRef}
      onClick={onClick}
      disabled={disabled}
      data-testid={testId}
      aria-expanded={open}
      aria-controls={controls}
      style={{
        width:        '100%',
        minHeight:    44,
        background:   t.surface,
        border:       `1px solid ${t.line}`,
        borderRadius: t.radius.md,
        padding:      '12px 14px',
        display:      'flex',
        alignItems:   'center',
        gap:          12,
        cursor:       disabled ? 'not-allowed' : 'pointer',
        opacity:      disabled ? 0.65 : 1,
        textAlign:    'left',
      }}
    >
      <span aria-hidden style={{ flex: '0 0 auto', display: 'inline-flex', color: t.accentInk }}>
        {icon}
      </span>
      <span style={{ flex: 1, minWidth: 0, fontFamily: t.fonts.body, fontSize: 14, fontWeight: 600, color: t.text }}>
        {label}
      </span>
      {sublabel && (
        <span style={{ fontFamily: t.fonts.body, fontSize: 12, color: t.textMuted, whiteSpace: 'nowrap' }}>
          {sublabel}
        </span>
      )}
      <span
        aria-hidden
        style={{
          fontFamily: t.fonts.mono,
          fontSize:   14,
          color:      t.textMuted,
          transform:  open ? 'rotate(90deg)' : 'rotate(0deg)',
          transition: 'transform .15s',
        }}
      >
        ›
      </span>
    </button>
  );
}

// ---------------------------------------------------------------------------
// Main ProfileScreen component
// ---------------------------------------------------------------------------

export default function ProfileScreen({ reloadPage = reloadBrowserPage }: ProfileScreenProps): JSX.Element {
  const tokens = useTokens();
  const { a11y, setA11y } = useTheme();
  const { goBack } = useShellNav();
  const [confirmLogout, setConfirmLogout] = useState(false);
  const [recoveryOpen, setRecoveryOpen] = useState(false);
  const [recoveryPassword, setRecoveryPassword] = useState('');
  const [recoveryKit, setRecoveryKit] = useState<RecoveryKit | null>(null);
  const [recoveryPending, setRecoveryPending] = useState(false);
  const [recoveryError, setRecoveryError] = useState<string | null>(null);
  const recoveryRequestIdRef = useRef(0);

  // Profile (name + exam date) — local state seeded from storage so an edit
  // re-renders the identity-card name + countdown immediately.
  const [profile, setProfile] = useState(() => getStoredProfile());
  const firstName = profile.firstName?.trim() || '';
  const initial = firstName ? firstName[0].toUpperCase() : '';
  const countdown = examCountdownLabel(profile.examDate);

  // "Modifica profilo" is a server-authoritative form. The identity card stays
  // on verified cached truth until a complete server result is written back.
  const [editOpen, setEditOpen] = useState(false);
  const [draftName, setDraftName] = useState(firstName);
  const [draftExamDate, setDraftExamDate] = useState(() => toDateInputValue(profile.examDate));
  const [editEpoch, setEditEpoch] = useState(0);
  const [saveState, setSaveState] = useState<ProfileSaveState>({ kind: 'idle' });
  const [validationField, setValidationField] = useState<ProfileValidationField>(null);
  const dateValidityRef = useRef<ParsedExamDate['kind']>(draftExamDate ? 'valid' : 'empty');
  const pendingRef = useRef(false);
  const mountedRef = useRef(false);
  const activeRequestRef = useRef<ActiveProfileRequest | null>(null);
  const editToggleRef = useRef<HTMLButtonElement>(null);
  const nameInputRef = useRef<HTMLInputElement>(null);
  const dateInputRef = useRef<HTMLInputElement>(null);
  const saveButtonRef = useRef<HTMLButtonElement>(null);
  const alertRef = useRef<HTMLParagraphElement>(null);
  const reloadActionRef = useRef<HTMLButtonElement>(null);

  const isPending = saveState.kind === 'pending';
  const isCacheLocked = saveState.kind === 'cache_failure';
  const formLocked = isPending || isCacheLocked;

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      const active = activeRequestRef.current;
      if (active) {
        active.controller.abort();
        if (activeRequestRef.current === active) activeRequestRef.current = null;
      }
      pendingRef.current = false;
    };
  }, []);

  // Focus only after React has committed the element/state that owns it.
  useEffect(() => {
    if (!mountedRef.current || confirmLogout) return;
    if (validationField === 'firstName') {
      nameInputRef.current?.focus();
      return;
    }
    if (validationField === 'examDate') {
      dateInputRef.current?.focus();
      return;
    }
    if (saveState.kind === 'success') editToggleRef.current?.focus();
    if (saveState.kind === 'conflict') alertRef.current?.focus();
    if (saveState.kind === 'retryable_failure') saveButtonRef.current?.focus();
    if (saveState.kind === 'cache_failure') reloadActionRef.current?.focus();
  }, [confirmLogout, saveState, validationField]);

  function reseedEditorFromProfile(nextProfile: ReturnType<typeof getStoredProfile>): void {
    const nextName = nextProfile.firstName?.trim() || '';
    const nextExamDate = toDateInputValue(nextProfile.examDate);
    setProfile(nextProfile);
    setDraftName(nextName);
    setDraftExamDate(nextExamDate);
    dateValidityRef.current = nextExamDate ? 'valid' : 'empty';
    setEditEpoch((value) => value + 1);
  }

  function toggleEdit(): void {
    if (pendingRef.current || isCacheLocked) return;
    if (editOpen) {
      setEditOpen(false);
      setValidationField(null);
      setSaveState({ kind: 'idle' });
      return;
    }

    reseedEditorFromProfile(getStoredProfile());
    setValidationField(null);
    setSaveState({ kind: 'idle' });
    setEditOpen(true);
  }

  function isActiveRequest(active: ActiveProfileRequest): boolean {
    return mountedRef.current
      && activeRequestRef.current === active
      && !active.controller.signal.aborted;
  }

  function handleDateValidity(kind: ParsedExamDate['kind']): void {
    dateValidityRef.current = kind;
    if (validationField === 'examDate' && (kind === 'empty' || kind === 'valid')) {
      setValidationField(null);
    }
  }

  function submitProfileEdit(event: FormEvent<HTMLFormElement>): void {
    event.preventDefault();
    if (pendingRef.current || isCacheLocked) return;

    if (dateValidityRef.current === 'partial' || dateValidityRef.current === 'invalid') {
      setSaveState({ kind: 'idle' });
      setValidationField('examDate');
      return;
    }

    const prepared = prepareStudentProfilePatch(getStoredProfile(), {
      firstName: draftName,
      examDateInput: draftExamDate,
    });

    if (prepared.status === 'invalid') {
      setSaveState({ kind: 'idle' });
      setValidationField(prepared.field);
      return;
    }
    if (prepared.status === 'unchanged') {
      setValidationField(null);
      setSaveState({ kind: 'unchanged' });
      return;
    }
    if (prepared.status === 'unavailable') {
      setValidationField(null);
      setSaveState({ kind: 'cache_failure', reason: 'missing_version' });
      return;
    }

    // This latch is deliberately set before the first await/state commit.
    pendingRef.current = true;
    const active: ActiveProfileRequest = {
      controller: new AbortController(),
      identity: Symbol('profile-request'),
    };
    activeRequestRef.current = active;
    setValidationField(null);
    setSaveState({ kind: 'pending' });

    void (async () => {
      try {
        const result = await studentProfileApi.update(prepared.request, active.controller.signal);
        if (!isActiveRequest(active)) return;

        const cacheResult = replaceStoredProfileFromPatchResult(result);
        if (!isActiveRequest(active)) return;
        if (cacheResult.status === 'failure') {
          setSaveState({
            kind: 'cache_failure',
            reason: result.status === 'updated' ? 'write_after_updated' : 'write_after_conflict',
          });
          return;
        }

        const cachedProfile = getStoredProfile();
        if (!isActiveRequest(active)) return;
        reseedEditorFromProfile(cachedProfile);
        setValidationField(null);
        if (result.status === 'updated') {
          setEditOpen(false);
          setSaveState({ kind: 'success' });
        } else {
          setEditOpen(true);
          setSaveState({ kind: 'conflict' });
        }
      } catch (error) {
        if (!isActiveRequest(active)) return;
        if (error instanceof ApiError && error.status === 401) return;
        if (error instanceof DOMException && error.name === 'AbortError') return;
        setValidationField(null);
        setSaveState({ kind: 'retryable_failure' });
      } finally {
        if (activeRequestRef.current === active) {
          activeRequestRef.current = null;
          pendingRef.current = false;
        }
      }
    })();
  }

  function closeRecoveryPanel(): void {
    recoveryRequestIdRef.current += 1;
    setRecoveryOpen(false);
    setRecoveryPassword('');
    setRecoveryKit(null);
    setRecoveryError(null);
    setRecoveryPending(false);
  }

  async function submitRecoveryRotation(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    if (!recoveryPassword || recoveryPending) return;
    const requestId = recoveryRequestIdRef.current + 1;
    recoveryRequestIdRef.current = requestId;
    setRecoveryPending(true);
    setRecoveryError(null);
    try {
      const kit = await authApi.rotateRecoveryKit({ currentPassword: recoveryPassword });
      if (recoveryRequestIdRef.current !== requestId) return;
      setRecoveryPassword('');
      setRecoveryKit(kit);
    } catch (error) {
      if (recoveryRequestIdRef.current !== requestId) return;
      setRecoveryError(
        error instanceof ApiError && error.status === 401
          ? 'La password attuale non è corretta.'
          : 'Non riesco a generare il kit. Riprova tra poco.',
      );
    } finally {
      if (recoveryRequestIdRef.current === requestId) setRecoveryPending(false);
    }
  }

  const politeCopy = saveState.kind === 'pending'
    ? 'Salvataggio in corso…'
    : saveState.kind === 'success'
      ? 'Profilo aggiornato.'
      : saveState.kind === 'unchanged'
        ? 'Non ci sono modifiche da salvare.'
        : '';

  const saveAlertCopy = saveState.kind === 'conflict'
    ? 'Il profilo è cambiato mentre lo stavi modificando. Ho caricato la versione più recente: controllala e ripeti le modifiche che vuoi salvare.'
    : saveState.kind === 'retryable_failure'
      ? 'Il salvataggio non è stato confermato. Il profilo mostrato non è cambiato: controlla i dati e riprova.'
      : saveState.kind === 'cache_failure' && saveState.reason === 'missing_version'
        ? 'Non posso verificare quale versione del profilo stai modificando. Ricarica l’app e riprova.'
        : saveState.kind === 'cache_failure' && saveState.reason === 'write_after_updated'
          ? 'Il profilo è stato salvato nel servizio, ma non riesco ad aggiornare i dati su questo dispositivo. Ricarica l’app prima di fare altre modifiche.'
          : saveState.kind === 'cache_failure' && saveState.reason === 'write_after_conflict'
            ? 'Il profilo è cambiato, ma non riesco ad aggiornare questo dispositivo con la versione più recente. Ricarica l’app prima di fare altre modifiche.'
            : '';

  const recoveryMustStayOpen = recoveryPending || recoveryKit !== null;
  useShellNavigationLock(recoveryMustStayOpen);

  return (
    <Screen>
      <TopChrome
        title="Profilo"
        subtitle="Impostazioni"
        onBack={() => { if (!recoveryMustStayOpen) goBack(); }}
      />

      <main
        aria-hidden={confirmLogout || undefined}
        style={{
          flex:          1,
          overflowY:     'auto',
          padding:       '20px 16px 32px',
          display:       'flex',
          flexDirection: 'column',
          gap:           24,
          width:         '100%',
          maxWidth:      560,
          margin:        '0 auto',
          boxSizing:     'border-box',
        }}
      >
        {/* Identity card */}
        <section
          data-testid="identity-card"
          style={{
            display:      'flex',
            alignItems:   'center',
            gap:          16,
            background:   tokens.surface,
            border:       `1px solid ${tokens.line}`,
            borderRadius: tokens.radius.lg,
            padding:      16,
          }}
        >
          {/* Rounded-SQUARE gradient avatar (profilo.html line 20: 52px / radius 14). */}
          <div
            aria-hidden
            style={{
              width:          52,
              height:         52,
              flexShrink:     0,
              borderRadius:   14,
              background:     tokens.brandGradient,
              display:        'flex',
              alignItems:     'center',
              justifyContent: 'center',
              color:          '#fff',
              fontFamily:     tokens.fonts.heading,
              fontSize:       20,
              fontWeight:     800,
            }}
          >
            {initial}
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div
              style={{
                fontFamily:    tokens.fonts.heading,
                fontSize:      20,
                fontWeight:    700,
                color:         tokens.text,
                letterSpacing: -0.2,
                whiteSpace:    'nowrap',
                overflow:      'hidden',
                textOverflow:  'ellipsis',
              }}
            >
              {firstName || 'Studente'}
            </div>
            {/* Subtitle: school (when known) · Patente B — always shown. */}
            <div
              style={{
                fontFamily:   tokens.fonts.body,
                fontSize:     13,
                color:        tokens.textMuted,
                marginTop:    3,
                whiteSpace:   'nowrap',
                overflow:     'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              {[profile.school, 'Patente B'].filter(Boolean).join(' · ')}
            </div>
            {/* Exam countdown on its own accent line, when a date is set. */}
            {countdown && (
              <div
                data-testid="profile-countdown"
                style={{
                  fontFamily: tokens.fonts.body,
                  fontSize:   13,
                  color:      tokens.accentInk,
                  fontWeight: 600,
                  marginTop:  2,
                }}
              >
                {countdown}
              </div>
            )}
          </div>
        </section>

        {/* Section: accessibility */}
        <section style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          <SectionLabel>Accessibilità</SectionLabel>
          <ToggleRow
            label="Font per dislessia"
            hint="Carattere OpenDyslexic: lettere con base più pesante per ridurre l'inversione."
            value={a11y.openDyslexic}
            onChange={(v) => setA11y({ openDyslexic: v })}
          />
          <ToggleRow
            label="Alto contrasto"
            hint="Aumenta il contrasto dei colori per una lettura più netta."
            value={a11y.highContrast}
            onChange={(v) => setA11y({ highContrast: v })}
          />
          <ToggleRow
            label="Riduci animazioni"
            hint="Disattiva transizioni e movimento. Utile se hai sensibilità al movimento."
            value={a11y.reducedMotion}
            onChange={(v) => setA11y({ reducedMotion: v })}
          />
        </section>

        {/* Section: account */}
        <section style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          <SectionLabel>Account</SectionLabel>

          {/* Modifica profilo — registration-page flow (name + data esame). The
              bare inline date input was retired (profilo.html lines 62-67); name
              + exam-date editing happens here, behind the chevron row. */}
          <ChevronRow
            testId="modifica-profilo"
            open={editOpen}
            onClick={toggleEdit}
            buttonRef={editToggleRef}
            disabled={formLocked}
            controls={PROFILE_PANEL_ID}
            label="Modifica profilo"
            sublabel="Nome · data esame"
            icon={
              <svg viewBox="0 0 24 24" width={20} height={20} fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round">
                <path d="M12 20h9" />
                <path d="M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4z" />
              </svg>
            }
          />
          {editOpen && (
            <form
              id={PROFILE_PANEL_ID}
              data-testid="modifica-profilo-panel"
              onSubmit={submitProfileEdit}
              aria-busy={isPending}
              style={{
                background:    tokens.surface,
                border:        `1px solid ${tokens.line}`,
                borderRadius:  tokens.radius.md,
                padding:       '12px 14px',
                display:       'flex',
                flexDirection: 'column',
                gap:           12,
              }}
            >
              {/* Nome */}
              <label
                htmlFor="profile-name-input"
                style={{ fontFamily: tokens.fonts.body, fontSize: 14, fontWeight: 600, color: tokens.text }}
              >
                Nome
              </label>
              <input
                id="profile-name-input"
                data-testid="profile-name-input"
                ref={nameInputRef}
                type="text"
                value={draftName}
                onChange={(e) => {
                  setDraftName(e.target.value);
                  if (validationField === 'firstName') setValidationField(null);
                }}
                disabled={formLocked}
                aria-invalid={validationField === 'firstName'}
                aria-describedby={validationField === 'firstName' ? 'profile-name-error' : undefined}
                placeholder="es. Marco"
                autoCapitalize="words"
                autoCorrect="off"
                style={{
                  width:        '100%',
                  minHeight:    44,
                  boxSizing:    'border-box',
                  background:   tokens.bg,
                  border:       `1px solid ${tokens.lineStrong}`,
                  borderRadius: tokens.radius.sm,
                  padding:      '8px 12px',
                  color:        tokens.text,
                  fontFamily:   tokens.fonts.body,
                  fontSize:     15,
                  marginTop:    -4,
                }}
              />
              {validationField === 'firstName' && (
                <p
                  id="profile-name-error"
                  role={confirmLogout ? undefined : 'alert'}
                  style={{ margin: '-6px 0 0', color: tokens.wrongText, fontFamily: tokens.fonts.body, fontSize: 12.5 }}
                >
                  {NAME_VALIDATION}
                </p>
              )}

              {/* Data esame */}
              <label
                htmlFor="exam-date-input"
                style={{ fontFamily: tokens.fonts.body, fontSize: 14, fontWeight: 600, color: tokens.text }}
              >
                Data esame
              </label>
              <ExamDateField
                key={editEpoch}
                value={draftExamDate}
                onChange={setDraftExamDate}
                disabled={formLocked}
                inputId="exam-date-input"
                inputTestId="exam-date-input"
                inputRef={dateInputRef}
                onValidityChange={handleDateValidity}
                validationError={validationField === 'examDate' ? DATE_VALIDATION : null}
              />
              <p style={{ fontFamily: tokens.fonts.body, fontSize: 12, color: tokens.textMuted, margin: 0, lineHeight: 1.4 }}>
                {DATE_HELP}
              </p>

              {/* Save */}
              <button
                type="submit"
                data-testid="save-profile"
                ref={saveButtonRef}
                disabled={formLocked}
                style={{
                  width:        '100%',
                  minHeight:    44,
                  background:   tokens.accent,
                  border:       'none',
                  borderRadius: tokens.radius.pill,
                  padding:      '12px 20px',
                  color:        '#fff',
                  fontFamily:   tokens.fonts.body,
                  fontSize:     14,
                  fontWeight:   600,
                  cursor:       formLocked ? 'not-allowed' : 'pointer',
                  opacity:      formLocked ? 0.65 : 1,
                }}
              >
                {saveState.kind === 'retryable_failure' ? 'Riprova' : 'Salva'}
              </button>
            </form>
          )}

          <div
            aria-live={confirmLogout ? 'off' : 'polite'}
            aria-atomic="true"
            role={!confirmLogout && politeCopy ? 'status' : undefined}
            style={{ fontFamily: tokens.fonts.body, fontSize: 13, color: tokens.textMuted }}
          >
            {politeCopy}
          </div>

          {saveAlertCopy && (
            <p
              ref={alertRef}
              role={confirmLogout ? undefined : 'alert'}
              tabIndex={saveState.kind === 'conflict' ? -1 : undefined}
              style={{
                margin: 0,
                padding: '10px 12px',
                border: `1px solid ${tokens.wrong}`,
                borderRadius: tokens.radius.sm,
                color: tokens.wrongText,
                fontFamily: tokens.fonts.body,
                fontSize: 13,
                lineHeight: 1.5,
              }}
            >
              {saveAlertCopy}
            </p>
          )}

          {saveState.kind === 'cache_failure' && (
            <button
              type="button"
              ref={reloadActionRef}
              onClick={reloadPage}
              style={{
                width: '100%',
                minHeight: 44,
                border: `1px solid ${tokens.lineStrong}`,
                borderRadius: tokens.radius.pill,
                background: tokens.surface,
                color: tokens.text,
                fontFamily: tokens.fonts.body,
                fontSize: 14,
                fontWeight: 600,
                cursor: 'pointer',
              }}
            >
              Ricarica l’app
            </button>
          )}

          {/* Informativa AI — reuses the approved Art. 13 disclosure wording */}
          <ChevronRow
            testId="profile-recovery-kit"
            open={recoveryOpen}
            onClick={() => {
              if (recoveryOpen) closeRecoveryPanel();
              else {
                setRecoveryOpen(true);
                setRecoveryError(null);
              }
            }}
            controls={RECOVERY_PANEL_ID}
            disabled={recoveryMustStayOpen}
            label="Kit di recupero"
            sublabel="Password dimenticata"
            icon={
              <svg viewBox="0 0 24 24" width={20} height={20} fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round">
                <circle cx="8" cy="15" r="4" />
                <path d="m11 12 8-8m-3 3 3 3m-6 0 3 3" />
              </svg>
            }
          />
          {recoveryOpen && (
            <div id={RECOVERY_PANEL_ID} style={{
              background: tokens.surface, border: `1px solid ${tokens.line}`,
              borderRadius: tokens.radius.md, padding: '12px 14px',
            }}>
              {recoveryKit ? (
                <RecoveryKitDisplay kit={recoveryKit} onDone={closeRecoveryPanel} doneLabel="Ho salvato il kit" />
              ) : (
                <form onSubmit={submitRecoveryRotation} style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                  <p style={{ margin: 0, color: tokens.textMuted, fontFamily: tokens.fonts.body, fontSize: 13, lineHeight: 1.5 }}>
                    Inserisci la password attuale. Il nuovo kit invaliderà tutti i codici precedenti.
                  </p>
                  <label style={{ color: tokens.text, fontFamily: tokens.fonts.body, fontSize: 14, fontWeight: 600 }}>
                    Password attuale
                  </label>
                  <PasswordField value={recoveryPassword} onChange={setRecoveryPassword}
                    ariaLabel="Password attuale" autoComplete="current-password" disabled={recoveryPending} />
                  {recoveryError && <p role="alert" style={{ margin: 0, color: tokens.wrongText, fontSize: 13 }}>{recoveryError}</p>}
                  <button type="submit" disabled={!recoveryPassword || recoveryPending} style={{
                    width: '100%', minHeight: 44, border: 'none', borderRadius: tokens.radius.pill,
                    background: tokens.accent, color: '#fff', fontFamily: tokens.fonts.body,
                    fontSize: 14, fontWeight: 700,
                  }}>
                    {recoveryPending ? 'Generazione…' : 'Genera un nuovo kit'}
                  </button>
                </form>
              )}
            </div>
          )}

          <InfoDisclosure label="Informativa AI" testId="informativa-ai">
            <span>
              AI Tutor ti aiuta a studiare la Patente B usando l&rsquo;intelligenza artificiale. Il Prof è un
              assistente AI, non un insegnante umano: può sbagliare, quindi verifica sempre sul Codice della
              Strada. Le tue domande, risposte e progressi vengono salvati per personalizzare il tuo percorso
              di studio.
            </span>
          </InfoDisclosure>

          {/* Privacy e dati */}
          <InfoDisclosure label="Privacy e dati" testId="privacy-dati">
            <span>{PRIVACY_DISCLOSURE}</span>
          </InfoDisclosure>

          {/* Esci (rose) */}
          <button
            data-testid="profile-logout"
            onClick={() => { if (!recoveryMustStayOpen) setConfirmLogout(true); }}
            disabled={recoveryMustStayOpen}
            style={{
              width:        '100%',
              minHeight:    44,
              background:   tokens.surface,
              border:       `1px solid ${tokens.wrong}`,
              borderRadius: tokens.radius.md,
              padding:      '12px 14px',
              color:        tokens.wrong,
              fontFamily:   tokens.fonts.body,
              fontSize:     14,
              fontWeight:   600,
              cursor:       recoveryMustStayOpen ? 'not-allowed' : 'pointer',
              opacity:      recoveryMustStayOpen ? 0.65 : 1,
              textAlign:    'center',
            }}
          >
            Esci
          </button>
        </section>

        {/* Footer */}
        <footer style={{ marginTop: 'auto', paddingTop: 24 }}>
          <p
            style={{
              fontFamily:    tokens.fonts.mono,
              fontSize:      10,
              color:         tokens.textFaint,
              textAlign:     'center',
              margin:        0,
              letterSpacing: 0.4,
            }}
          >
            {/* V29 S2: the real build stamp — was a hardcoded "v2.0" that
                never changed across releases. */}
            AI Tutor · Autoscuola AI · {__BUILD_ID__}
          </p>
        </footer>
      </main>

      <StudentLogoutModal
        open={confirmLogout}
        onCancel={() => setConfirmLogout(false)}
      />
    </Screen>
  );
}
